// Pickup Availability Component - Flexible Selectors
class PickupAvailability {
  constructor() {
    // console.log('PickupAvailability initializing with flexible selectors');
    
    // Try multiple possible selectors for the store availability container
    this.container = 
      document.querySelector('[data-section-type="store-availability"]') || 
      document.querySelector('.store-availability') ||
      document.querySelector('#StoreAvailabilityModal') ||
      document.querySelector('.product__store-availability-container');
    
    // console.log('Container found:', !!this.container);
    
    // Get drawer element by ID or class
    this.drawer = 
      document.getElementById('StoreAvailabilityDrawer') || 
      document.querySelector('.store-availabilities-modal');
    
    // console.log('Drawer found:', !!this.drawer);
    
    // If neither container nor drawer exists, don't proceed
    if (!this.container && !this.drawer) {
      // console.log('No store availability elements found on page');
      return;
    }
    
    // Initialize component
    this.init();
  }
  
  init() {
    // console.log('Initializing pickup availability component');
    
    // Check if user has a preferred store
    this.userStoreId = localStorage.getItem('userStoreId');
    this.userStoreName = localStorage.getItem('userStoreName');
    
    // console.log('User store from localStorage:', this.userStoreName);
    
    // Add event listeners
    this.setupEventListeners();
    
    // Set up mutation observer to watch for content changes
    this.setupMutationObserver();
    
    // If drawer exists, add store selection buttons
    if (this.drawer) {
      // console.log('Adding store selection buttons to drawer');
      this.addStoreSelectionButtons();
    }
    
    // IMPORTANT: If user has a preferred store, prioritize it in both main display and drawer
    if (this.userStoreId && this.userStoreName) {
      // console.log('User has preferred store, updating displays');
      
      // Update main availability display
      if (this.container) {
        this.updateMainAvailability();
      }
      
      // Reorder drawer items to show preferred store first
      if (this.drawer) {
        this.reorderDrawerStores();
      }
    }
  }
  
  setupEventListeners() {
    // Listen for store selection events from other components
    document.addEventListener('storeSelected', (event) => {
      // console.log('Pickup availability received store selection event:', event.detail);
      if (event.detail && event.detail.storeId) {
        this.userStoreId = event.detail.storeId;
        this.userStoreName = event.detail.storeName;
        
        // Update main availability display
        this.updateMainAvailability();
        
        // Reorder drawer if it exists
        if (this.drawer) {
          this.reorderDrawerStores();
        }
        
        // If drawer is open, update buttons
        if (this.drawer && this.isDrawerOpen()) {
          this.updateDrawerStoreButtons();
        }
      }
    });
    
    // Listen for various possible drawer opener selectors
    const possibleOpeners = [
      '.js-drawer-open-availability',
      '[aria-controls="StoreAvailabilityDrawer"]',
      '[data-store-availability-modal-opener]'
    ];
    
    possibleOpeners.forEach(selector => {
      const openers = document.querySelectorAll(selector);
      openers.forEach(opener => {
        opener.addEventListener('click', () => {
          // console.log('Drawer opener clicked, updating store buttons');
          // Wait a bit for drawer to open
          setTimeout(() => {
            this.addStoreSelectionButtons();
            this.updateDrawerStoreButtons();
          }, 300);
        });
      });
    });
    
    // Listen for location changes to update the drawer
    document.addEventListener('locationChanged', (event) => {
      // console.log('Pickup availability received locationChanged event:', event.detail);
      
      // If drawer is open, wait a bit then update buttons
      if (this.drawer && this.isDrawerOpen()) {
        setTimeout(() => {
          this.addStoreSelectionButtons();
          this.updateDrawerStoreButtons();
        }, 300);
      }
    });
  }
  
  // Set up mutation observer to watch for pickup availability content changes
  setupMutationObserver() {
    // Watch for changes in the pickup availability container
    if (this.container || this.drawer) {
      this.observer = new MutationObserver((mutations) => {
        let hasRelevantChanges = false;
        
        mutations.forEach((mutation) => {
          // Check if this is relevant to pickup availability
          if (mutation.type === 'childList') {
            // Check if store availability content was added/removed
            const addedNodes = Array.from(mutation.addedNodes);
            const removedNodes = Array.from(mutation.removedNodes);
            
            const isRelevant = [...addedNodes, ...removedNodes].some(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                return node.classList.contains('store-availability') ||
                       node.querySelector('.store-availability') ||
                       node.id === 'StoreAvailabilityDrawer' ||
                       node.dataset.sectionType === 'store-availability';
              }
              return false;
            });
            
            if (isRelevant) {
              hasRelevantChanges = true;
            }
          }
        });
        
        if (hasRelevantChanges && this.userStoreId && this.userStoreName) {
          // console.log('Pickup availability content changed, updating preferred store display');
          
          // Re-find elements in case they changed
          this.container = 
            document.querySelector('[data-section-type="store-availability"]') || 
            document.querySelector('.store-availability') ||
            document.querySelector('#StoreAvailabilityModal') ||
            document.querySelector('.product__store-availability-container');
          
          this.drawer = 
            document.getElementById('StoreAvailabilityDrawer') || 
            document.querySelector('.store-availabilities-modal');
          
          // Wait a bit for content to settle then update
          setTimeout(() => {
            if (this.drawer) {
              this.addStoreSelectionButtons();
            }
            if (this.container) {
              this.updateMainAvailability();
            }
            if (this.drawer) {
              this.reorderDrawerStores();
            }
          }, 200);
        }
      });
      
      // Start observing
      const targetElement = document.querySelector('main') || document.body;
      this.observer.observe(targetElement, {
        childList: true,
        subtree: true
      });
      
      // console.log('Mutation observer set up for pickup availability changes');
    }
  }
  
  // Helper to check if drawer is open
  isDrawerOpen() {
    if (!this.drawer) return false;
    
    // Check various ways a drawer might be open
    return (
      this.drawer.getAttribute('aria-hidden') === 'false' ||
      this.drawer.classList.contains('is-active') ||
      this.drawer.classList.contains('drawer--is-open') ||
      this.drawer.hasAttribute('open') ||
      window.getComputedStyle(this.drawer).display !== 'none'
    );
  }
  
  addStoreSelectionButtons() {
    // Only proceed if the drawer exists
    if (!this.drawer) {
      // console.log('No drawer found, cannot add store buttons');
      return;
    }
    
    // console.log('Looking for store items in drawer');
    
    // Try different selectors for store items in the drawer
    const storeItems = 
      this.drawer.querySelectorAll('.store-availability') || 
      this.drawer.querySelectorAll('[data-store-availability-container]') ||
      this.drawer.querySelectorAll('.store-availabilities-list__item');
    
    // console.log(`Found ${storeItems.length} store items`);
    
    if (storeItems.length === 0) {
      return;
    }
    
    storeItems.forEach((item, index) => {
      // Try to find store name from multiple possible elements
      const storeNameElement = 
        item.querySelector('strong') || 
        item.querySelector('.store-availability-name') ||
        item.querySelector('h3');
      
      if (!storeNameElement) {
        // console.log('Store name element not found in item', index);
        return;
      }
      
      const storeName = storeNameElement.textContent.trim();
      
      // Create a unique ID for the store if it doesn't have one
      let storeId = item.dataset.storeId;
      if (!storeId) {
        storeId = `store-${index}-${storeName.replace(/\s+/g, '-').toLowerCase()}`;
        item.setAttribute('data-store-id', storeId);
        item.setAttribute('data-store-name', storeName);
      }
      
      // Check if there are coordinates for this store
      let storeCoordinates = null;
      
      // Try to find the store in the global storeLocations array
      if (typeof window.storeLocations !== 'undefined') {
        // Try to match by store name
        const matchingStore = window.storeLocations.find(store => {
          // Normalize store names for comparison
          const name1 = store.name.toLowerCase().replace(/[^a-z0-9]/g, '');
          const name2 = storeName.toLowerCase().replace(/[^a-z0-9]/g, '');
          return name1 === name2 || name1.includes(name2) || name2.includes(name1);
        });
        
        if (matchingStore && matchingStore.coordinates) {
          storeCoordinates = matchingStore.coordinates;
          
          // Store coordinates in the DOM element for future reference
          item.dataset.latitude = matchingStore.coordinates.lat;
          item.dataset.longitude = matchingStore.coordinates.lng;
        }
      }
      
      // Get the address for display in the store info
      let storeAddress = '';
      const addressElements = item.querySelectorAll('address');
      if (addressElements.length > 0) {
        // Use the text content of the address element
        storeAddress = addressElements[0].textContent.trim();
      }
      
      // Find a suitable container for the button within the store item
      const storeInfo = 
        item.querySelector('.store-availability__info') || 
        item.querySelector('.store-availabilities-list__location-info') ||
        item.querySelector('address') ||
        item;
      
      // Calculate distance if coordinates are available
      let distanceDisplay = '';
      if (storeCoordinates) {
        const userCoordinates = this.getUserCoordinates();
        if (userCoordinates) {
          const distance = window.calculateDistance(
            userCoordinates.lat, userCoordinates.lng,
            storeCoordinates.lat, storeCoordinates.lng
          );
          
          // Format the distance
          if (distance < 0.1) {
            distanceDisplay = `${distance.toFixed(2)} miles away`;
          } else if (distance < 1) {
            distanceDisplay = `${distance.toFixed(1)} miles away`;
          } else {
            distanceDisplay = `${Math.round(distance * 10) / 10} miles away`;
          }
          
          // Add distance to the item
          const distanceElement = item.querySelector('.store-distance');
          if (!distanceElement) {
            const distanceDiv = document.createElement('div');
            distanceDiv.className = 'store-distance';
            distanceDiv.style.cssText = 'font-size: 12px; color: #666; margin-top: 4px;';
            distanceDiv.textContent = distanceDisplay;
            storeInfo.appendChild(distanceDiv);
          } else {
            distanceElement.textContent = distanceDisplay;
          }
        }
      }
      
      // Only add button if it doesn't already exist
      if (!item.querySelector('.store-availability-favorite')) {
        // console.log(`Adding button for store: ${storeName}`);
        
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'store-availability__small store-availability__small--link';
        buttonContainer.innerHTML = `
          <button class="store-availability-favorite btn btn--small"
                  type="button"
                  aria-label="Set ${storeName} as your location"
                  data-store-id="${storeId}"
                  data-store-name="${storeName}">
            Set as my store
          </button>
        `;
        
        storeInfo.appendChild(buttonContainer);
        
        // Add click event listener
        const button = buttonContainer.querySelector('button');
        button.addEventListener('click', (e) => {
          e.preventDefault();
          // console.log(`Store button clicked: ${storeName}`);
          this.setUserStore(storeId, storeName);
        });
      }
    });
    
    this.updateDrawerStoreButtons();
  }
  
  // Helper method to get user coordinates
  getUserCoordinates() {
    // Check localStorage first
    const savedCoordinates = localStorage.getItem('userSearchCoordinates');
    if (savedCoordinates) {
      try {
        const coords = JSON.parse(savedCoordinates);
        if (coords && coords.latitude && coords.longitude) {
          return {
            lat: coords.latitude,
            lng: coords.longitude
          };
        }
      } catch (e) {
        // console.error('Error parsing saved coordinates:', e);
      }
    }
    
    // If we have userLocation from the store locator
    if (typeof userLocation !== 'undefined' && userLocation) {
      return userLocation;
    }
    
    return null;
  }
  
  setUserStore(storeId, storeName) {
    // console.log(`Setting user store: ${storeName} (${storeId})`);
    
    // Save to localStorage
    localStorage.setItem('userStoreId', storeId);
    localStorage.setItem('userStoreName', storeName);
    
    // Update the UI
    this.userStoreId = storeId;
    this.userStoreName = storeName;
    
    // Update main availability display with new preferred store
    this.updateMainAvailability();
    
    // Reorder drawer to show new preferred store first
    this.reorderDrawerStores();
    
    // Update drawer buttons
    this.updateDrawerStoreButtons();
    
    // Notify other components
    document.dispatchEvent(new CustomEvent('storeSelected', {
      detail: { storeId, storeName }
    }));
    
    // Try to close the drawer (support multiple possible close button selectors)
    setTimeout(() => {
      const closeButton = 
        this.drawer.querySelector('.js-drawer-close') ||
        this.drawer.querySelector('[data-modal-close]') ||
        this.drawer.querySelector('.close-modal');
      
      if (closeButton) {
        closeButton.click();
      }
    }, 300);
  }
  
  updateMainAvailability() {
    if (!this.container || !this.userStoreId) return;
    
    // console.log('Updating main availability display for preferred store');
    
    // Find main availability container
    const mainAvailabilityContainer = 
      this.container.querySelector('.store-availability') ||
      this.container.querySelector('[data-store-availability-container]');
    
    if (!mainAvailabilityContainer) {
      // console.log('Main availability container not found');
      return;
    }
    
    // Find the preferred store in the drawer to get availability info
    let preferredStoreAvailability = null;
    if (this.drawer) {
      // Look for store items in drawer that match our preferred store
      const drawerStoreItems = this.drawer.querySelectorAll('.store-availability');
      for (const item of drawerStoreItems) {
        const strongElement = item.querySelector('strong');
        if (strongElement && strongElement.textContent.trim() === this.userStoreName) {
          preferredStoreAvailability = item;
          break;
        }
      }
    }
    
    // Update the main display with preferred store info
    if (preferredStoreAvailability) {
      // console.log('Found preferred store in drawer, updating main display');
      
      // Clone the preferred store's availability info
      const preferredInfo = preferredStoreAvailability.cloneNode(true);
      
      // Update the main container's content
      const mainInfo = mainAvailabilityContainer.querySelector('.store-availability__info');
      if (mainInfo) {
        // Clear existing content and replace with preferred store info
        const newInfo = preferredInfo.querySelector('.store-availability__info');
        if (newInfo) {
          mainInfo.innerHTML = newInfo.innerHTML;
          
          // Add preferred store indicator
          if (!mainInfo.querySelector('.preferred-store-badge')) {
            const badge = document.createElement('div');
            badge.className = 'preferred-store-badge';
            badge.style.cssText = 'font-size: 11px; color: #666; margin-top: 4px; font-style: italic;';
            badge.textContent = '(My preferred store)';
            mainInfo.appendChild(badge);
          }
        }
      }
      
      // Update the icon to match the preferred store's availability
      const mainIcon = mainAvailabilityContainer.querySelector('svg');
      const preferredIcon = preferredStoreAvailability.querySelector('svg');
      if (mainIcon && preferredIcon) {
        mainIcon.className = preferredIcon.className;
        mainIcon.innerHTML = preferredIcon.innerHTML;
      }
    } else {
      // Fallback to just updating the store name
      const storeNameElement = 
        mainAvailabilityContainer.querySelector('.store-availability__info div strong') ||
        mainAvailabilityContainer.querySelector('.store-availability__info div') ||
        mainAvailabilityContainer.querySelector('.store-name');
      
      if (storeNameElement) {
        if (storeNameElement.tagName === 'STRONG') {
          storeNameElement.textContent = this.userStoreName;
        } else {
          storeNameElement.innerHTML = `<strong>${this.userStoreName}</strong>`;
        }
        // console.log('Updated store name in main display');
        
        // Add preferred store indicator
        const infoContainer = mainAvailabilityContainer.querySelector('.store-availability__info');
        if (infoContainer && !infoContainer.querySelector('.preferred-store-badge')) {
          const badge = document.createElement('div');
          badge.className = 'preferred-store-badge';
          badge.style.cssText = 'font-size: 11px; color: #666; margin-top: 4px; font-style: italic;';
          badge.textContent = '(My preferred store)';
          infoContainer.appendChild(badge);
        }
      }
    }
    
    // Add distance info if available
    const infoContainer = mainAvailabilityContainer.querySelector('.store-availability__info');
    if (infoContainer && typeof window.storeLocations !== 'undefined') {
      // Try to find matching store
      const matchingStore = window.storeLocations.find(store => 
        store.id === this.userStoreId ||
        store.name === this.userStoreName
      );
      
      if (matchingStore) {
        // Add or update a distance indicator if we have coordinates
        const userCoordinates = this.getUserCoordinates();
        if (userCoordinates && matchingStore.coordinates) {
          const distance = window.calculateDistance(
            userCoordinates.lat, userCoordinates.lng,
            matchingStore.coordinates.lat, matchingStore.coordinates.lng
          );
          
          // Format distance with better precision
          let distanceText = '';
          if (distance < 0.1) {
            distanceText = `${distance.toFixed(2)} miles away`;
          } else if (distance < 1) {
            distanceText = `${distance.toFixed(1)} miles away`;
          } else {
            distanceText = `${Math.round(distance * 10) / 10} miles away`;
          }
          
          // Find or create the distance element
          let distanceElement = infoContainer.querySelector('.store-distance');
          if (!distanceElement) {
            distanceElement = document.createElement('div');
            distanceElement.className = 'store-distance';
            distanceElement.style.cssText = 'font-size: 12px; color: #666; margin-top: 4px;';
            infoContainer.appendChild(distanceElement);
          }
          
          distanceElement.textContent = distanceText;
        }
      }
    }
  }
  
  // New method to reorder drawer stores to show preferred store first
  reorderDrawerStores() {
    if (!this.drawer || !this.userStoreId) return;
    
    // console.log('Reordering drawer stores to show preferred store first');
    
    // Find the scrollable container
    const scrollableContainer = this.drawer.querySelector('.drawer__scrollable');
    if (!scrollableContainer) {
      // console.log('Scrollable container not found in drawer');
      return;
    }
    
    // Find all store availability items
    const storeItems = scrollableContainer.querySelectorAll('.store-availability');
    if (storeItems.length === 0) {
      // console.log('No store items found in drawer');
      return;
    }
    
    // Find the preferred store item
    let preferredStoreItem = null;
    for (const item of storeItems) {
      const strongElement = item.querySelector('strong');
      if (strongElement && strongElement.textContent.trim() === this.userStoreName) {
        preferredStoreItem = item;
        break;
      }
    }
    
    if (preferredStoreItem) {
      // console.log('Found preferred store in drawer, moving to top');
      
      // Find the parent container of store items
      const itemsContainer = preferredStoreItem.parentElement;
      
      // Move preferred store to the top
      itemsContainer.insertBefore(preferredStoreItem, itemsContainer.firstChild);
      
      // Add a visual indicator that this is the preferred store
      if (!preferredStoreItem.querySelector('.preferred-store-indicator')) {
        const indicator = document.createElement('div');
        indicator.className = 'preferred-store-indicator';
        indicator.style.cssText = 'background: #e8f5e8; border-left: 3px solid #00a14b; padding: 4px 8px; margin: -8px -8px 8px -8px; font-size: 11px; color: #00a14b; font-weight: bold;';
        indicator.textContent = 'My Preferred Store';
        
        preferredStoreItem.insertBefore(indicator, preferredStoreItem.firstChild);
      }
    } else {
      // console.log('Preferred store not found in drawer items');
    }
  }
  
  updateDrawerStoreButtons() {
    if (!this.drawer || !this.userStoreId) return;
    
    // console.log('Updating drawer store buttons for store ID:', this.userStoreId);
    
    // Reset all buttons
    const allButtons = this.drawer.querySelectorAll('.store-availability-favorite');
    allButtons.forEach(button => {
      button.textContent = 'Set as my store';
      button.classList.remove('store-availability-favorite--enabled');
    });
    
    // Update the selected store button
    const selectedStore = this.drawer.querySelector(`[data-store-id="${this.userStoreId}"]`);
    if (selectedStore) {
      const selectedButton = selectedStore.querySelector('.store-availability-favorite');
      if (selectedButton) {
        selectedButton.textContent = 'My store';
        selectedButton.classList.add('store-availability-favorite--enabled');
        // console.log('Updated button for preferred store');
      }
      
      // Try to move selected store to the top if possible
      const parentList = selectedStore.parentElement;
      if (parentList && (parentList.tagName === 'UL' || parentList.classList.contains('drawer__scrollable'))) {
        parentList.prepend(selectedStore);
        // console.log('Moved preferred store to top of list');
      }
    } else {
      // console.log('Selected store not found in drawer:', this.userStoreId);
    }
  }
  
  // Diagnostic function
  diagnosePickupAvailability() {
    // console.group('Pickup Availability Diagnostics');
    // console.log('Container found:', !!this.container);
    // console.log('Drawer found:', !!this.drawer);
    // console.log('User store from localStorage:', {
    //   userStoreId: localStorage.getItem('userStoreId'),
    //   userStoreName: localStorage.getItem('userStoreName')
    // });
    // console.log('Component user store:', {
    //   userStoreId: this.userStoreId,
    //   userStoreName: this.userStoreName
    // });
    
    const storeItems = this.drawer ? 
      this.drawer.querySelectorAll('[data-store-id]').length : 
      'No drawer available';
    // console.log('Store items with IDs:', storeItems);
    
    // console.groupEnd();
  }
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  // console.log('DOM loaded, checking for store availability');
  
  // Wait a bit longer to make sure all elements are fully loaded
  setTimeout(() => {
    const pickupAvailability = new PickupAvailability();
    
    // Add to window for diagnostic access
    window.pickupAvailability = pickupAvailability;
    
    // Run diagnostics after initialization
    setTimeout(() => {
      try {
        pickupAvailability.diagnosePickupAvailability();
      } catch (e) {
        // console.error('Error in pickup availability diagnostics:', e);
      }
    }, 1000);
  }, 500);
});

// Also initialize when page content updates (for ajax page loads)
document.addEventListener('page:loaded section:loaded', () => {
  // console.log('Page or section loaded, reinitializing pickup availability');
  setTimeout(() => {
    const pickupAvailability = new PickupAvailability();
    window.pickupAvailability = pickupAvailability;
  }, 500);
});

// Handle variant changes with multiple possible event names
function handleVariantChange(eventName) {
  // console.log(`${eventName} triggered, updating pickup availability`);
  
  // Don't create a new instance, just update the existing one after DOM updates
  setTimeout(() => {
    if (window.pickupAvailability) {
      // console.log('Updating existing pickup availability instance');
      
      // Re-find DOM elements in case they changed
      window.pickupAvailability.container = 
        document.querySelector('[data-section-type="store-availability"]') || 
        document.querySelector('.store-availability') ||
        document.querySelector('#StoreAvailabilityModal') ||
        document.querySelector('.product__store-availability-container');
      
      window.pickupAvailability.drawer = 
        document.getElementById('StoreAvailabilityDrawer') || 
        document.querySelector('.store-availabilities-modal');
      
      // console.log('Refreshed container:', !!window.pickupAvailability.container);
      // console.log('Refreshed drawer:', !!window.pickupAvailability.drawer);
      
      // Re-read user preferences from localStorage in case they changed
      window.pickupAvailability.userStoreId = localStorage.getItem('userStoreId');
      window.pickupAvailability.userStoreName = localStorage.getItem('userStoreName');
      
      // If user has a preferred store, update displays
      if (window.pickupAvailability.userStoreId && window.pickupAvailability.userStoreName) {
        // console.log('Updating displays for preferred store after variant change');
        
        // Add store selection buttons to new drawer content
        if (window.pickupAvailability.drawer) {
          window.pickupAvailability.addStoreSelectionButtons();
        }
        
        // Update main availability display
        if (window.pickupAvailability.container) {
          // Wait a bit more for the availability content to fully load
          setTimeout(() => {
            window.pickupAvailability.updateMainAvailability();
          }, 200);
        }
        
        // Reorder drawer items
        if (window.pickupAvailability.drawer) {
          setTimeout(() => {
            window.pickupAvailability.reorderDrawerStores();
          }, 300);
        }
      }
    } else {
      // console.log('No existing pickup availability instance, creating new one');
      const pickupAvailability = new PickupAvailability();
      window.pickupAvailability = pickupAvailability;
    }
  }, 500);
}

// Listen for multiple possible variant change events
document.addEventListener('variant:changed', () => handleVariantChange('variant:changed'));
document.addEventListener('variantchange', () => handleVariantChange('variantchange'));
document.addEventListener('product:variant-change', () => handleVariantChange('product:variant-change'));

// Also listen for pickup availability specific updates
document.addEventListener('pickup-availability:updated', () => {
  // console.log('Pickup availability content updated');
  if (window.pickupAvailability && window.pickupAvailability.userStoreId) {
    setTimeout(() => {
      window.pickupAvailability.updateMainAvailability();
      window.pickupAvailability.reorderDrawerStores();
    }, 100);
  }
});

// Add global calculateDistance fallback if not available from store locator
if (typeof window.calculateDistance !== 'function') {
  window.calculateDistance = function(lat1, lon1, lat2, lon2) {
    // Validate inputs
    if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
        typeof lat2 !== 'number' || typeof lon2 !== 'number' ||
        isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
      // console.warn('Invalid coordinates in distance calculation:', { lat1, lon1, lat2, lon2 });
      return Infinity;
    }
    
    const R = 3958.8; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance;
  };
}

// Initialize immediately as well (backup)
// // console.log('Running initial pickup availability check');
setTimeout(() => {
  const pickupAvailability = new PickupAvailability();
  window.pickupAvailability = pickupAvailability;
}, 1000);